/** One emoji in the picker data (emojiData.json, generated by scripts/generate-emoji.mjs). */
export type EmojiEntry = [emoji: string, name: string, keywords: string]

export interface EmojiGroup {
  name: string
  /** Stands for the group in the picker's group bar. */
  icon: string
  emoji: EmojiEntry[]
}

interface Indexed {
  entry: EmojiEntry
  order: number
  name: string
  nameWords: string[]
  keywords: string[]
}

const words = (text: string) => text.toLowerCase().split(/\s+/).filter(Boolean)

/** Search by the starts of words in names and keywords; every word of the query must match. */
export function createEmojiIndex(groups: EmojiGroup[]) {
  const items: Indexed[] = groups
    .flatMap((group) => group.emoji)
    .map((entry, order) => ({ entry, order, name: entry[1].toLowerCase(), nameWords: words(entry[1]), keywords: words(entry[2]) }))

  return {
    search(query: string): EmojiEntry[] {
      const trimmed = query.trim()
      const terms = words(trimmed)
      if (!terms.length) return []
      const phrase = terms.join(' ')
      const ranked: { item: Indexed; rank: number }[] = []
      for (const item of items) {
        const wordInName = (term: string) => item.nameWords.includes(term)
        const startInName = (term: string) => item.nameWords.some((w) => w.startsWith(term))
        let rank: number
        if (item.entry[0] === trimmed || item.name === phrase) rank = 0
        else if (terms.every(wordInName)) rank = 1
        else if (terms.every((term) => wordInName(term) || item.keywords.includes(term))) rank = 2
        else if (terms.every(startInName)) rank = 3
        else if (terms.every((term) => startInName(term) || item.keywords.some((w) => w.startsWith(term)))) rank = 4
        else continue
        ranked.push({ item, rank })
      }
      return ranked.sort((a, b) => a.rank - b.rank || a.item.order - b.item.order).map(({ item }) => item.entry)
    },
  }
}
